import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { OrderPaymentPatchBody } from '@vs-shop/schemas/orderPayment';
import type {
  OrderPaymentId,
  PaymentMethodCode,
} from '../../../service/orderShipment/OrderShipment';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/payments/{paymentId}';
const HTTP_METHOD = 'patch';
type PaymentPatch = OrderPaymentPatchBody & { readonly paymentMethod: PaymentMethodCode };

export type MakeUpdatePaymentInOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
};

export const makeUpdatePaymentInOrder = <OShowType>(
  deps: MakeUpdatePaymentInOrderDeps<OShowType>
) => {
  const UpdatePaymentError = createBackendApiError({
    domain: 'Order',
    endpoint: 'UpdatePayment',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const patchOrderPaymentFetch = (
    tokenValue: string,
    paymentId: OrderPaymentId,
    data: PaymentPatch
  ) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/merge-patch+json')
        .fetch({ params: { tokenValue, paymentId: String(paymentId) }, body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
          'payment.id': paymentId,
          'payment.method': data.paymentMethod,
        },
      })
    );

  const updatePaymentInOrder = (
    tokenValue: string,
    paymentId: OrderPaymentId,
    data: PaymentPatch
  ) =>
    patchOrderPaymentFetch(tokenValue, paymentId, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(new UpdatePaymentError.NotFoundError({ orderToken: tokenValue, response })),
          422: UpdatePaymentError.createViolationError(tokenValue),
          orElse: (response) =>
            Effect.fail(
              new UpdatePaymentError.ApiRequestError({ orderToken: tokenValue, response })
            ),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderPaymentUpdate,
          'Debug',
          `Successfully updated payment method to ${data.paymentMethod} for order ${tokenValue}`
        )
      ),
      Effect.withSpan('api.orderPayment.update', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
          'payment.id': paymentId,
          'payment.method': data.paymentMethod,
        },
      })
    );

  return { updatePaymentInOrder, UpdatePaymentError };
};
